import type { TowerDesign } from "@/game/towerSpec";

/**
 * FAR CRY 3 RADIO TOWER — the iconic climbable radio tower you activate to reveal
 * the map. Not a real-world engineering reference: a game prop, modeled here as a
 * proper square (4-leg) self-support lattice so it stands in the solver.
 *
 * Proportions are taken from the actual in-game mesh, extracted from Far Cry 3's
 * Dunia archives (multicommon.dat → radiotower_bottom/middle/top_01.xbg) and
 * measured: three stacked, X-braced sections tapering from a wide splayed-leg base
 * through a mid platform deck to a slim antenna mast. Section face-width taper from
 * the mesh (bottom 73→46, middle 33→26, top 14→pt, arbitrary units) is reproduced
 * as a continuous square taper scaled to a ~36 m tower. The mid platform and the
 * upper antenna deck are the game model's walkway and its dish/antenna cluster.
 *
 * Members: A572-50 pipe legs, A36 angle bracing — conventional light self-support
 * steel, sized so the coarse lattice stands comfortably under self-weight and wind.
 */

// square lattice: circumradius (centre→corner) from a face width (m)
const R = (faceM: number) => faceM / Math.SQRT2;

export const design: TowerDesign = {
  id: "farcry3-radio-tower",
  name: "Far Cry 3 Radio Tower",
  description: "The climbable Far Cry 3 radio tower — square self-support lattice, proportions from the extracted game mesh.",
  author: "game prop — geometry measured from the extracted Far Cry 3 radiotower .xbg mesh",
  spec: {
    type: "lattice",
    height: 36,
    segments: {
      faces: 4, // square, four splayed legs
      baseRadius: R(4.8), // ~4.8 m base face → wide splayed base
      sections: [
        // wide braced base — the climbable lower run
        {
          toHeight: 12, topRadius: R(3.2), panels: 4,
          legSection: "legHeavy", braceSection: "braceMed",
          legMaterial: "a572", braceMaterial: "a36", bracing: "K",
        },
        // tapering middle — up to the platform deck
        {
          toHeight: 24, topRadius: R(1.8), panels: 4,
          legSection: "legMed", braceSection: "braceMed",
          legMaterial: "a572", braceMaterial: "a36", bracing: "X",
        },
        // slim upper mast carrying the antenna cluster
        {
          toHeight: 36, topRadius: R(0.7), panels: 4,
          legSection: "legMed", braceSection: "braceLight",
          legMaterial: "a572", braceMaterial: "a36", bracing: "diagonal",
        },
      ],
    },
    // the walkway platform partway up, and the antenna/dish deck near the top
    crossarms: [
      { atHeightFrac: 0.63, radius: 2.1 }, // the climb-to platform deck
      { atHeightFrac: 0.9, radius: 1.1 }, // upper antenna/dish array
    ],
    foundation: {
      soil: "dense-sand",
      base: { type: "pad", width: 2.6, diameter: 1, depth: 2.4, concrete: "c30" }, // per-leg spread footing
      connection: {
        kind: "anchorBolts", nBolts: 8, bolt: "b1_5", grade: "f1554_55",
        boltCircleDia: 0.7, embedment: 0.8, edgeDistance: 0.35,
        platePlanArea: 0.5, plateThickness: 0.045, plateGrade: "a36rod", fc: 30e6,
      },
    },
  },
};
